import { defaultServiceContext } from '../contexts';
import IntentUsedIn from './usedIn/IntentUsedIn';
import ModuleUsedIn from './usedIn/ModuleUsedIn';
import ObjectsUsedIn from './usedIn/ObjectsUsedIn';
import Utility from './Utility';
import NewElementDialog from './../widgets/dialogs/NewElementDialog';
import { Constant } from '../const';
import { parseCommonModule, resetCommonModuleLocalVariablesNotSensitive } from './functions';

const { avsService } = defaultServiceContext;

export default class CommonModuleManager {

    constructor(){
        this.initializeChangeVersion = this.initializeChangeVersion.bind(this);
        this.previousPrompts = {};
        this.previousApplicationInputValues = {};
        this.previousNodesInputValues = {};
        this.previousInstancesInputValues = {};
        this.cacheIntents = [];
    }

    initializeCommonModule(commonModule, existingModule){
        
        this.newRootModuleId = Utility.uuid4();
        this.moduleAppName = (existingModule) ? existingModule.name : commonModule.application.toUpperCase();

        this.deleteDuplicatePrompts(commonModule);
        this.getNormalizedCommonModule(commonModule);
        this.resetComponentModulesProps(commonModule);
        this.resetFlowNodesAndFlowLinksIds(commonModule);
        this.resetComponentModulesIds(commonModule);
        this.resetComponentObjectsIds(commonModule);
        this.resetComponentIntents(commonModule);
        this.mergeIntentsCacheConfig(commonModule);
        this.resetGraphqlSchemasIds(commonModule);
        this.resetCsvDataIds(commonModule);
        // TODO: These will require a rerender of Settings page
        // window.apm.reactDOMComponents.render({ name: 'SettingWidget', props: { serialized: window.apm.serialized }, $el: $('#settings') });
        this.insertGraphQLSchemas(commonModule);
        this.insertCSVData(commonModule);
        this.commonModuleRemarking(commonModule);
        //upgrade app variables to OmniChannel, if applicable
        this.upgradeVariableCheck(commonModule);
        //upgrade prompts to OmniChannel, if applicable
        this.upgradePromptConfigForSimultaneous(commonModule);
        return commonModule;
        
    }

    /**
     * Adds `sensitiveOverrides` to application serialized
     * @param {object} serialized 
     * @returns 
     */
    initializeSensitivityOverrides(serialized){
        serialized.sensitiveOverrides = {
            enabled: false,
            lastLocalChange: null
        };

        return serialized;
    }

    /**
     * This function adds "commonModuleName" and "commonModuleVersion" to properties.
     * This is for the main imported Common Module and any nested Common Modules. 
     * These properties are used in export to help PE with error logging.
     * @param {object} commonModule
     */

    commonModuleRemarking(commonModule) {
        commonModule.components.modules.forEach((mod) => {   
            if(mod.hasOwnProperty('repoName')){
                mod.props.commonModuleName = mod.repoName;
                mod.props.commonModuleVersion = mod.props.config.version;
            }
            if(mod.versions){
                const nodeFlow = mod.versions[1].flow.nodes;

                nodeFlow.forEach(n=>{
                    const parentMod = commonModule.components.modules.filter(p => p.id === n.props.parentComponentID)[0];

                    if(parentMod && parentMod.hasOwnProperty('repoName')) {
                        n.props.commonModuleName = parentMod.repoName;
                        n.props.commonModuleVersion = parentMod.props.config.version;
                    }
                });
            }
        });
    }
/**
 * Omnichannel upgrade for prompts
 * @param {*} commonModule 
 */
    upgradePromptConfigForSimultaneous(commonModule){
        const { isOmniChannel } = window.apm.serialized.omniChannel;

        if(isOmniChannel){
            // upgrade application prompt configs 
            commonModule.components.objects.filter(obj => obj.props.nodeTypeName === "Prompt").forEach(prompt => {
                if(!prompt.props.config.hasOwnProperty("channel")){
                    window.apm.knowledgeBase.initializePromptChannelConfig(prompt);
                }
            });
            // upgrade flow node prompt configs 
            commonModule.components.modules.forEach(module=>{
            module.versions[1].flow.nodes.forEach(node=>{
                if(node.props.nodeTypeName === "Prompt"){
                    if(!node.props.config.hasOwnProperty("channel")){
                        window.apm.knowledgeBase.initializePromptChannelConfig(node);
                    }
                }
            })
        })
    }
}

    /**
     * @param {Design} commonModule
     * @param {DesignModule} importedModule
     * @param {boolean} disableRedaction
     */
    importCommonModule(rawCommonModule, importedModule = null, disableRedaction = null) {
        console.time('init');
        const commonModule = parseCommonModule(rawCommonModule);

        // from NewElementDialog UI
        if(importedModule) {
            this.initializeCommonModule(commonModule, importedModule);
        } else {
            this.initializeCommonModule(commonModule);
        }
        this.importCommonModuleComponents(commonModule);
        this.registerCommonModule(commonModule, false);
        if (disableRedaction) {
            const serialized = window.apm.serialized;
            resetCommonModuleLocalVariablesNotSensitive(this.newRootModuleId, serialized);
        }
        window.apm.postEventToCurrentView({
            target: "request-component-panel", 
            event: {id: this.newRootModuleId },
            shouldFocusInput: true,
        });
        console.timeEnd('init');
    }

    registerCommonModule(commonModule, replace){
        window.apm.registerCommonModule(commonModule, replace);
    }

    /**
     * @param {string} repoName
     * @param {string} selectedVersion 
     * @param {string} nodeName
     * @param {string} nodeId 
     */
    async initializeChangeVersion(repoName, selectedVersion, nodeName, nodeId){
        const commonModule = await avsService.getCommonModuleDesignByName(repoName, selectedVersion);
        const previousCommonModule = window.apm.serialized.components.modules.find(
          ({ id, name, repoName }) => id === nodeId && name === nodeName && repoName === commonModule.application
        );

        // present Common Module install options before changing versions
        if(commonModule.sensitiveOverrides && commonModule.sensitiveOverrides.enabled){
            document.dispatchEvent(new CustomEvent("ev-dialog-show-common-module-install-options", {detail: {commonModule, previousCommonModule, type: "changeVersion"}}));
        }
        // default, no install options available on this Common Module
        else {
            this.changeCommonModuleVersion(commonModule, previousCommonModule);

            document.dispatchEvent(new CustomEvent('ev-dialog-close'));
            w2popup.close();
        }
    }

    // from property panel version dropdown
    changeCommonModuleVersion(commonModule, previousCommonModule, disableRedaction = null) {
        // TODO: test before acceptance
        this.cachePreviousCommonModule = _.cloneDeep(previousCommonModule);
        this.cacheIntents = _.cloneDeep(window.apm.serialized.components.intents);
        //Prepare incoming moduleto be compatible with OmniChannel, if applicable
        this.upgradeVariableCheck(commonModule);
        this.upgradePromptConfigForSimultaneous(commonModule)

        window.apm.postEventToCurrentView({target: "request-hide-property-panel"});
        this.previousPrompts = this.setPreviousPrompts(previousCommonModule.id);
        this.previousApplicationInputValues = this.setPreviousApplicationInputValues(previousCommonModule);
        this.previousNodesInputValues = this.setPreviousNodesInputValues(previousCommonModule);
        this.previousInstancesInputValues = this.setPreviousInstancesInputValues(previousCommonModule);
        this.removeCommonModule(previousCommonModule.id, false);
        this.initializeCommonModule(commonModule, previousCommonModule);
        this.importCommonModuleComponents(commonModule);
        // initializing common module before applying cachePrompt Text fixes the issue with DEVDES2-1700 as we are deleting the prompts Dup
        this.maintainModuleFoldersAndDescription(previousCommonModule, this.newRootModuleId);
        this.replaceModuleInApplicationFlows(previousCommonModule);
        this.applyCachedPromptText(previousCommonModule.name);
        this.applyCachedPromptInputs(previousCommonModule.name);
        this.applyPreviousInputValues(previousCommonModule);
        this.applyPreviousOutputValues(previousCommonModule);
        this.registerCommonModule(commonModule, true);

        if (disableRedaction) {
            const serialized = window.apm.serialized;
            resetCommonModuleLocalVariablesNotSensitive(this.newRootModuleId, serialized);
        }

        // refresh the canvas to show the modified data which took place on each apply fn
        window.apm.postEventToAllViews({target: "request-reload-data-model"});

        // show the property panel for the application common module
        window.apm.postEventToCurrentView({target: "request-component-panel", event: {id: this.newRootModuleId }});
    }

    renameModuleApp(commonModuleId, newName){
        this.renameGraphQLSchemas(commonModuleId, newName);
        this.renameCSVData(commonModuleId, newName);
        this.renameComponents(commonModuleId, newName);
        this.renameFlowNodesAndFlowLinks(commonModuleId, newName);
    }

    renameGraphQLSchemas(commonModuleId, newName){
        if(window.apm.serialized.graphQLSchemas){
            window.apm.serialized.graphQLSchemas.forEach(schema => {
                if(schema.moduleId && schema.moduleId === commonModuleId){
                    schema.moduleApp = newName;
                    schema.lastLocalChange = window.apm.timestamp;
                }
            })
        }
    }
    renameCSVData(commonModuleId, newName){
        if(window.apm.serialized.csvData){
            window.apm.serialized.csvData.forEach(csv => {
                if(csv.moduleId && csv.moduleId === commonModuleId){
                    csv.moduleApp = newName;
                    csv.lastLocalChange = window.apm.timestamp;
                }
            })
        }
    }

    renameComponents(commonModuleId, newName){
        window.apm.serialized.components.modules.forEach(mod => {
            if(mod.props.moduleId && mod.props.moduleId === commonModuleId){
                mod.props.moduleApp = newName;
                mod.props.lastLocalChange = window.apm.timestamp;
            }        
        });

        window.apm.serialized.components.objects.forEach(obj => {
            if(obj.props.moduleId && obj.props.moduleId === commonModuleId){
                obj.props.moduleApp = newName;
                obj.props.lastLocalChange = window.apm.timestamp;
                if(obj.props.nodeTypeName === 'Prompt'){
                    obj.wftName = newName + '_' + obj.name;
                }
            }
            
        });

        window.apm.serialized.components.intents.forEach(int => {
            if(int.props.moduleId && int.props.moduleId === commonModuleId){
                int.props.moduleApp = newName;
                int.props.lastLocalChange = window.apm.timestamp;
            }

        });

    }

    renameFlowNodesAndFlowLinks(commonModuleId, newName){
        window.apm.serialized.components.modules.forEach(mod => {
            if(mod.props.moduleId && mod.props.moduleId === commonModuleId){
                mod.versions[1].flow.nodes.forEach(node => {
                    node.props.moduleApp = newName;
                    node.props.lastLocalChange = window.apm.timestamp;
                });
            }
            
        });
    }

    // DEVDES2-1700: When a Common module version is updated it will reset all the prompts in it's internal modules
    // TODO: this is the same as AppManager.cleanUpCommonModuleDuplicatePromptsInSerialized
    deleteDuplicatePrompts(serialized){
        serialized.components.objects.filter(object => object.props.nodeTypeName === "Prompt" && object.isHidden).forEach(prompt => {
            // all prompts matched by name
            const PROMPTS_BY_NAME = serialized.components.objects.filter(object => object.props.nodeTypeName === "Prompt" && object.name === prompt.name);
            
            // clean up duplicate prompts
            if(PROMPTS_BY_NAME.length > 1){
                PROMPTS_BY_NAME.forEach(matchedPrompt => {
                    const usedIn = ObjectsUsedIn.getObjectUsedIn(matchedPrompt.id, serialized);
                    
                    // TODO: ???
                    // if it is not used, delete from app and add to deletedMap
                    if(usedIn[0].length === 0){
                        serialized.components.objects = serialized.components.objects.filter(obj => obj.id !== matchedPrompt.id);
                    }
                    
                });
            }
        
        });
    }

    removeCommonModule(existingModuleId, removeFromFlows = true){
        this.removeModuleFromCanvas(existingModuleId);
        // CM TODO: GraphQLSchema delete maps
        this.removeGraphQLSchemas(existingModuleId);
        this.removeCSVData(existingModuleId);        
        this.removeComponents(existingModuleId);
        if(removeFromFlows){
            this.removeModuleFromApplicationFlows(existingModuleId);
        }
        window.apm.postEventToCurrentView({target: "request-hide-property-panel"});
    }

    removeModuleFromCanvas(existingModuleId){
        // CM TODO: close all open cm-related module tabs
        window.apm.closeTabById(existingModuleId);

        const clientCacheHasModule = window.apm.cache.get(Constant.k.NAMESPACE_CLIENT, existingModuleId + '_canvas')
        const diagramCahceHasModule = window.apm.cache.get(Constant.k.NAMESPACE_DIAGRAM_WIDGET, existingModuleId + '_canvas');
        
        if(clientCacheHasModule){
            window.apm.cache.remove(Constant.k.NAMESPACE_CLIENT, existingModuleId + '_canvas')
        }

        if(diagramCahceHasModule){
            window.apm.cache.remove(Constant.k.NAMESPACE_DIAGRAM_WIDGET, existingModuleId + '_canvas');
        };
    }

    removeComponents(existingModuleId){
        const treeDom = $('#tl_container');
        const $jstree = treeDom.jstree();
        
        // delete original cm modules
        window.apm.serialized.components.modules = window.apm.serialized.components.modules.filter(mod => {    
            if(mod.props.moduleId === existingModuleId){
                // add to delete maps for collab
                window.apm.updateDeletedApplicationObjectsMap('modules', mod.id);
                // remove from app panel
                $jstree.delete_node(mod.id);
            } else {
                return mod;
            }
        });
        
        // delete original cm objects
        window.apm.serialized.components.objects = window.apm.serialized.components.objects.filter(obj => {            
            if(obj.props.moduleId === existingModuleId){
                // add to delete maps for collab
                window.apm.updateDeletedApplicationObjectsMap('objects', obj.id);
                // remove from app panel 
                $jstree.delete_node(obj.id);  
            } else {
                return obj;
            }
        });
        
        // delete original cm intents
        window.apm.serialized.components.intents = window.apm.serialized.components.intents.filter(intent => {
            if(intent.props.moduleId === existingModuleId){
                // add to delete maps for collab
                window.apm.updateDeletedApplicationObjectsMap('intents', intent.id);
                // remove from app panel 
                $jstree.delete_node(intent.id);  
            } else {
                return intent;
            }
        });
    }

    getUpdatedModuleInputs(currentInputs, incomingModuleInputs){
        const updatedModuleInputs = [];

        incomingModuleInputs.forEach(incomingInput => {
            const currentInput = currentInputs.find(ci => ci.argumentName === incomingInput.argumentName);

            if(currentInput){
                incomingInput.type = currentInput.type; // value or variable
                incomingInput.value = currentInput.value; // value vale
                incomingInput.varName = currentInput.varName; // variable value
            } 

            updatedModuleInputs.push(incomingInput);

        });

        return updatedModuleInputs;
    }

    removeModuleFromApplicationFlows(existingModuleId){
        window.apm.removeNode(existingModuleId);
    }

    replaceModuleInApplicationFlows(previousCommonModule){
        const usedInFlows = ModuleUsedIn.getModuleUsedInModuleFlows(previousCommonModule, window.apm.serialized.components);
        const commonModuleId = this.newRootModuleId;
        usedInFlows[0].forEach(modId => {
            const mod = window.apm.serialized.components.modules.find(m => m.id === modId);
            const nodes = mod.versions[1].flow.nodes.filter(node => node.props.nodeTypeName === 'Module' && node.props.parentComponentID === previousCommonModule.id);
            

            nodes.forEach(node => {
                const parentModule = window.apm.serialized.components.modules.find(mod => mod.id === commonModuleId);
                const nodeOutputs = _.cloneDeep(node.props.config.outputs);
                const nodeNewConfig = _.cloneDeep({
                    ...parentModule.props.config,
                    outputs: nodeOutputs
                });

                if (nodeNewConfig.outputs == null) {
                    delete nodeNewConfig.outputs;
                }

                node.props.parentComponentID = this.newRootModuleId;
                node.props.ref = this.newRootModuleId;
                node.props.moduleId = this.newRootModuleId;
                node.props.config = nodeNewConfig;
                node.props.lastLocalChange = window.apm.timestamp;
            });

            /**
             * if this function is meant to be used outside from CommonModuleManager,
             * then the `postEventToAllViews` function below needs to be called
             */
            // window.apm.postEventToAllViews({target: "request-reload-data-model"});

        });
    }

    setPreviousPrompts(existingModuleId){
        const previousPrompts = window.apm.serialized.components.objects
            .filter(obj => {
                return obj.props.moduleId === existingModuleId && obj.props.nodeTypeName === 'Prompt'
            });

        return previousPrompts;
    }

    setPreviousApplicationInputValues(previousCommonModule) {
        return {
            [previousCommonModule.props.commonModuleName]: previousCommonModule.props.config.inputs
        };
    }

    setPreviousNodesInputValues(module, inputs = {}) {
        module.versions[1].flow.nodes
            .filter(n => n.props.nodeTypeName === "Module")
            .forEach(mod => {
                if (!mod.props) return;

                const moduleName = mod.props.commonModuleName || mod.name;
                inputs[moduleName] = mod.props.config.inputs;

                const applicationModule = window.apm.serialized.components.modules.find(m => m.id === mod.props.parentComponentID);

                // legacy fix, fixes issue with missing items in imported common modules
                if (applicationModule) {
                    this.setPreviousNodesInputValues(applicationModule, inputs);
                }
            });

        return inputs;
    }

    setPreviousInstancesInputValues(previousCommonModule) {
        const inputs = {};

        const usedInFlows = ModuleUsedIn.getModuleUsedInModuleFlows(previousCommonModule, window.apm.serialized.components);
        usedInFlows[0].forEach(modId => {
            const mod = _.cloneDeep(window.apm.serialized.components.modules.find(m => m.id === modId));
            const nodes = mod.versions[1].flow.nodes
                .filter(node => node.props.nodeTypeName === 'Module' && node.props.parentComponentID === previousCommonModule.id);

            inputs[modId] = nodes;
        });

        return inputs;
    }

    applyCachedPromptText(commonModuleName) {
        const incomingApplicationModule = window.apm.serialized.components.modules.find(module => module.name === commonModuleName);
        
        window.apm.serialized.components.objects
            .filter(obj => obj.props.moduleId === incomingApplicationModule.id && obj.props.nodeTypeName === 'Prompt')
            .forEach(incomingPrompt => {
                const previousPrompt = this.previousPrompts.find(p => p.name === incomingPrompt.name);   
                
                if (previousPrompt) {
                    incomingPrompt.props.config.text = previousPrompt.props.config.text;
                    incomingPrompt.props.config.channel = previousPrompt.props.config.channel;
                    incomingPrompt.props.config.channelLayer = previousPrompt.props.config.channelLayer;

                    const promptUsedIn = ObjectsUsedIn.getObjectUsedIn(incomingPrompt.id, window.apm.serialized);

                    // modules where the incoming prompt is being used in
                    promptUsedIn[0].forEach(modId => {
                        const mod = window.apm.serialized.components.modules.find(mod => mod.id === modId);

                        mod.versions[1].flow.nodes
                            .filter(node => node.name === incomingPrompt.name && node.props.nodeTypeName === 'Prompt')
                            .forEach(incomingPromptNode => {
                                incomingPromptNode.props.config.text = previousPrompt.props.config.text;
                                incomingPromptNode.props.config.channel = previousPrompt.props.config.channel;
                                incomingPromptNode.props.config.channelLayer = previousPrompt.props.config.channelLayer;
                            });
                    });
                }
            });
    }

    applyCachedPromptInputs(commonModuleName) {
        const incomingApplicationModule = window.apm.serialized.components.modules.find(module => module.name === commonModuleName);
        
        window.apm.serialized.components.objects
            .filter(obj => obj.props.moduleId === incomingApplicationModule.id && obj.props.nodeTypeName === 'Prompt')
            .forEach(incomingPrompt => {
                const previousPrompt = this.previousPrompts.find(p => p.name === incomingPrompt.name);   
                
                if (previousPrompt) {
                    incomingPrompt.props.config.inputs = previousPrompt.props.config.inputs;
                    incomingPrompt.props.config.dynamic = previousPrompt.props.config.dynamic;
                    incomingPrompt.props.config.allowFallbackDomain = previousPrompt.props.config.allowFallbackDomain;

                    const promptUsedIn = ObjectsUsedIn.getObjectUsedIn(incomingPrompt.id, window.apm.serialized);

                    // modules where the incoming prompt is being used in
                    promptUsedIn[0].forEach(modId => {
                        const mod = window.apm.serialized.components.modules.find(mod => mod.id === modId);

                        mod.versions[1].flow.nodes
                            .filter(node => node.name === incomingPrompt.name && node.props.nodeTypeName === 'Prompt')
                            .forEach(incomingPromptNode => {
                                incomingPromptNode.props.config.inputs = previousPrompt.props.config.inputs;
                                incomingPromptNode.props.config.dynamic = previousPrompt.props.config.dynamic;
                                incomingPromptNode.props.config.allowFallbackDomain = previousPrompt.props.config.allowFallbackDomain;
                            });
                    });
                }
            });
    }

    applyPreviousNodesInputValues(module) {
        module.versions[1].flow.nodes
            .filter(n => n.props.nodeTypeName === "Module")
            .forEach(mod => {
                if (!mod.props) return;

                const localModuleName = mod.props.commonModuleName || mod.name;
                const previousLocalModuleInputs = this.previousNodesInputValues[localModuleName];

                // legacy fix, fixes issue with missing items in imported common modules
                if (previousLocalModuleInputs) {
                    mod.props.config.inputs.forEach((moduleInput, index) => {
                        const previousInput = previousLocalModuleInputs.find(i => i.argumentName === moduleInput.argumentName);

                        if (previousInput) {
                            mod.props.config.inputs[index] = previousInput;
                        }
                    });
                }

                const applicationModule = window.apm.serialized.components.modules.find(m => m.id === mod.props.parentComponentID);
                // legacy fix, fixes issue with missing items in imported common modules
                if (!applicationModule) return;

                const applicationModuleName = applicationModule.props.commonModuleName || applicationModule.name;
                const previousApplicationModuleInputs = this.previousNodesInputValues[applicationModuleName];

                // if there are previously saved inputs for this application module, set them in the incoming imported inputs
                // legacy fix, fixes issue with missing items in imported common modules
                if (previousApplicationModuleInputs) {
                    applicationModule.props.config.inputs.forEach((moduleInput, index) => {
                        const previousInput = previousApplicationModuleInputs.find(i => i.argumentName === moduleInput.argumentName);

                        if (previousInput) {
                            applicationModule.props.config.inputs[index] = previousInput;
                        }
                    });
                }

                // apply same process to the `applicationModule` flow nodes (recursive)
                this.applyPreviousNodesInputValues(applicationModule);
            });
    }

    applyPreviousInputValues(previousCommonModule) {
        const incomingApplicationModule = window.apm.serialized.components.modules.find(module => module.name === previousCommonModule.name);

        // apply to common module
        incomingApplicationModule.props.config.inputs.forEach((moduleInput, index) => {
            const previousCommonModuleInput = this.previousApplicationInputValues[incomingApplicationModule.props.commonModuleName]
                .find(i => i.argumentName === moduleInput.argumentName);

            if (previousCommonModuleInput) {
                incomingApplicationModule.props.config.inputs[index] = previousCommonModuleInput;
            }
        });

        // apply to common module flow nodes (nested common modules)
        this.applyPreviousNodesInputValues(incomingApplicationModule);

        // apply to common module instances
        const usedInFlows = ModuleUsedIn.getModuleUsedInModuleFlows(incomingApplicationModule, window.apm.serialized.components);
        usedInFlows[0].forEach(modId => {
            const mod = window.apm.serialized.components.modules.find(m => m.id === modId);
            const nodes = mod.versions[1].flow.nodes
                .filter(node => node.props.nodeTypeName === 'Module' && node.props.parentComponentID === incomingApplicationModule.id);
            const previousNodes = this.previousInstancesInputValues[modId]; // previous common module instance(s) inside this `mod`.

            if (!previousNodes) return;

            nodes.forEach(node => {
                const previousNode = previousNodes.find(prevNode => prevNode.id === node.id);

                node.props.config.inputs.forEach((nodeInput, index) => {
                    const previousNodeInput = previousNode.props.config.inputs
                        .find(i => i.argumentName === nodeInput.argumentName);

                    if (previousNodeInput) {
                        nodeInput.type = previousNodeInput.type;
                        nodeInput.value = previousNodeInput.value;
                        nodeInput.varName = previousNodeInput.varName;
                    }
                });
            });
        });

        /**
         * if this function is meant to be used outside from CommonModuleManager,
         * then the `postEventToAllViews` function below needs to be called
         */
        // window.apm.postEventToAllViews({target: "request-reload-data-model"});
    }

    applyPreviousOutputValues(previousCommonModule) {
        const incomingApplicationModule = window.apm.serialized.components.modules.find(module => module.name === previousCommonModule.name);
        const previousOutputs = previousCommonModule.props.config.outputs;

        // apply previous outputs to incoming application module
        if (previousOutputs != null) {
            incomingApplicationModule.props.config.outputs = previousOutputs;
        }

        /**
         * if this function is meant to be used outside from CommonModuleManager,
         * then the `postEventToAllViews` function below needs to be called
         */
        // window.apm.postEventToAllViews({target: "request-reload-data-model"});
    }
    
    importCommonModuleComponents(commonModule){
        const treeDom = $('#tl_container');
        const hasSensitiveOverrides = commonModule.hasOwnProperty("sensitiveOverrides") || null;
        const sensitiveOverrides = commonModule.sensitiveOverrides;
        const { serialized, timestamp } = window.apm;

        commonModule.components.objects.forEach(obj => {
            if(obj.props.nodeTypeName === 'Prompt'){
                obj.wftName = this.moduleAppName + '_' + obj.name;
                if (serialized.omniChannel.isOmniChannel) {
                    window.apm.knowledgeBase.initializePromptChannelConfig(obj)
                }
            }
            NewElementDialog.addElement(treeDom, obj, true, obj.id);
        });

        commonModule.components.intents.forEach(int => {
            NewElementDialog.addElement(treeDom, int, true, int.id);
        });

        commonModule.components.modules.forEach(mod => {
            if(mod.props.isRootModule && hasSensitiveOverrides){
                sensitiveOverrides.lastLocalChange = timestamp;
                mod.props.config["sensitiveOverrides"] = sensitiveOverrides;
            }
            NewElementDialog.addElement(treeDom, mod, true, mod.id);
        });
    }

    getNormalizedCommonModule(commonModule){
        delete commonModule.fillerPrompts;
        delete commonModule.globalErrorHandling;
        delete commonModule.globalExceptionHandling;
        delete commonModule.previousActiveTab;
        delete commonModule.currentActiveTab;
        delete commonModule.dtmf_live_agent_override_pattern;
        delete commonModule.languages;
        delete commonModule.voice;
        delete commonModule.language;
        delete commonModule.lastUpdate;
        delete commonModule.designer;

        return commonModule;
    }

    insertGraphQLSchemas(commonModule){
        window.apm.importGraphQLSchemasFromCommonModule(commonModule, this.newRootModuleId, this.moduleAppName);
    }

    insertCSVData(commonModule){
        window.apm.importCSVDataFromCommonModule(commonModule, this.newRootModuleId, this.moduleAppName);
    }


    removeGraphQLSchemas(id){
        window.apm.removeGraphQLSchemasFromCommonModule(id);
    }

    removeCSVData(id){
        window.apm.removeCSVDataFromCommonModule(id);
    }

    resetComponentModulesProps(commonModule){
        const newRootModuleId = this.newRootModuleId;

        const {
            allowSessionVariableAccess = false
        } = commonModule.rootSettings;
        commonModule.components.modules.forEach((mod) => {    
            const isRootModule = mod.id === 'rootModule';

            mod.props.iconClass = "globe";
            mod.props.readOnly = true;
            mod.props.isCommonModule = true;
            mod.props.isRootModule = isRootModule;
            mod.isHidden = !isRootModule; // hides dependent modules from Application Panel
            mod.props.hasWizardConfig= commonModule.wizardConfig?true:false;
            if (isRootModule) {
                // rename start module to common module name (e.g., Start to CM name)
                mod.name = this.moduleAppName;
                mod.id = newRootModuleId;
                mod.repoName = commonModule.application;
                mod.repoVersion = commonModule.version;
                mod.props.config = {
                    ...mod.props.config,
                    allowSessionVariableAccess,
                    version: commonModule.version
                };
            } 

            mod.props.createdTime = window.apm.timestamp;
            mod.props.lastLocalChange = window.apm.timestamp;
            
        });
    }
    
    resetFlowNodesAndFlowLinksIds(commonModule){
        const newRootModuleId = this.newRootModuleId;

        commonModule.components.modules.forEach(mod => {
            const flowNodes = mod.versions[1].flow.nodes
            
            flowNodes.forEach(node => {
                const newId = Utility.uuid4();
                const links = mod.versions[1].flow.links || [];
                
                // reset target and source for links
                links.filter(link => link.target === node.id).forEach(link => link.target = newId);
                links.filter(link => link.source === node.id).forEach(link => link.source = newId);
                // reset node id and props
                node.id = newId;
                node.props.moduleId = newRootModuleId;
                node.props.moduleApp = this.moduleAppName;
                node.props.isCommonModule = true;
            });

            const flowLinks = mod.versions[1].flow.links || [];
            flowLinks.forEach(link => link.id = Utility.uuid4());
        
        });
    }

    resetComponentModulesIds(commonModule){
        const newRootModuleId = this.newRootModuleId;

        commonModule.components.modules.forEach(mod => {
            const newModuleId = Utility.uuid4();
            const usedInFlows = ModuleUsedIn.getModuleUsedInModuleFlows(mod, commonModule.components);
            const usedInExitNodes = ModuleUsedIn.getModuleUsedInExitNodes(mod, commonModule.components);
            const isRootModule = mod.props.isRootModule;

            // usedInFlows
            if(usedInFlows[0].length > 0){
                usedInFlows[0].forEach(modId => {
                    const foundIn = commonModule.components.modules.find(m => m.id === modId);
                    const instanceMods = foundIn.versions[1].flow.nodes.filter(node => node.props.parentComponentID === mod.id);
                    instanceMods.forEach(instanceMod => {
                        instanceMod.props.parentComponentID = newModuleId;
                        instanceMod.props.ref = newModuleId;
                    });
                })
            }

            // usedInExitNodes
            if(usedInExitNodes.length > 0){
                usedInExitNodes.forEach(extObj => {
                    if(extObj.exitNode.props.config.exitComponent && extObj.exitNode.props.config.exitComponent.handlingModule){
                        const exitNode = commonModule.components.objects.find(obj => obj.id === extObj.exitNode.id);

                        exitNode.props.config.exitComponent.handlingModule = newModuleId;

                        const exitNodeUsedIn = ObjectsUsedIn.getObjectUsedIn(exitNode.id, commonModule);
                        const exitNodeUsedInFlows = exitNodeUsedIn[0];
                        
                        if(exitNodeUsedInFlows.length > 0){
                            exitNodeUsedInFlows.forEach(modId => {
                                const usedInMod = commonModule.components.modules.find(mod => mod.id === modId);
                                // TODO: should this be using an id vs "ExitNodeModel"? 
                                const nodes = usedInMod.versions[1].flow.nodes.filter(node => node._class === 'ExitNodeModel');

                                nodes.forEach(node => {
                                    if(node.props.config.exitComponent && node.props.config.exitComponent.handlingModule === mod.id){
                                        node.props.config.exitComponent.handlingModule = newModuleId;
                                    }
                                })
                                
                            })
                        }

                    }
                })
            }

            // usedInModuleInputs
            if(usedInFlows[1].length > 0){}

            // usedInLocalVariables
            if(usedInFlows[2].length > 0){}
            const assignedModId = (isRootModule) ? newRootModuleId : newModuleId;
            
            mod.id = assignedModId;
            mod.props.moduleId = newRootModuleId;
            mod.props.moduleApp = this.moduleAppName;
            if (mod.localVariables) {
                mod.localVariables.forEach(lv => {
                    lv.props.parentModule = assignedModId;
                    lv.props.createdTime = window.apm.timestamp;
                    lv.props.lastLocalChange= window.apm.timestamp;
                })
            }
        });

    }

    resetComponentObjectsIds(commonModule){
        const newRootModuleId = this.newRootModuleId;
   
        commonModule.components.objects.forEach(object => {
            const newObjId = Utility.uuid4();
            const usedIn = ObjectsUsedIn.getObjectUsedIn(object.id, commonModule);
            const savedPrompts = commonModule.components.objects.filter(obj => obj._class === "SavePromptNodeModel");
            const playSavedPrompts = commonModule.components.objects.filter(obj => obj._class === "PlaySavedPromptNodeModel");

            // usedInFlows
            if(usedIn[0].length > 0) {
                // find object instances in mod flows, update their parentCompnentID
                usedIn[0].forEach(modId => {
                    const foundIn = commonModule.components.modules.find(m => m.id === modId);
                    const nodes = foundIn.versions[1].flow.nodes.filter(node => node.props.parentComponentID === object.id);
                    nodes.forEach(node => {
                        node.props.parentComponentID = newObjId;
                        
                        if(node.props.nodeTypeName === 'Prompt'){
                            // find prompt refs in all saved prompts and play saved prompts
                            savedPrompts.forEach(sp => {
                                if(sp.props.config.promptId === object.id){
                                    sp.props.config.promptId = newObjId;

                                    // replace in flows
                                    const savedPromptUsedIn = ObjectsUsedIn.getObjectUsedIn(sp.id, commonModule);
                                    savedPromptUsedIn[0].forEach(modId => {
                                        const mod = commonModule.components.modules.find(mod => mod.id === modId);
                                        const savedPromptNodes = mod.versions[1].flow.nodes.filter(node => node.props.nodeTypeName === "Save Prompt");

                                        savedPromptNodes.forEach(spn => {
                                            if(spn.props.config.promptId && spn.props.config.promptId === object.id){
                                                spn.props.config.promptId = newObjId;
                                            }
                                        })
                                    })
                                }
                            });

                            playSavedPrompts.forEach(psp => {
                                if(psp.props.config.promptId === object.id){
                                    psp.props.config.promptId = newObjId;

                                    // replace in flows
                                    const playSavedPromptUsedIn = ObjectsUsedIn.getObjectUsedIn(psp.id, commonModule);
                                    playSavedPromptUsedIn[0].forEach(modId => {
                                        const mod = commonModule.components.modules.find(mod => mod.id === modId);
                                        const playSavedPromptNodes = mod.versions[1].flow.nodes.filter(node => node.props.nodeTypeName === "Play Saved Prompt");

                                        playSavedPromptNodes.forEach(pspn => {
                                            if(pspn.props.config.promptId && pspn.props.config.promptId === object.id){
                                                pspn.props.config.promptId = newObjId;
                                            }
                                        })
                                    })
                                }
                            })
                        }
                        
                        if(node.props.nodeTypeName === "Play Saved Prompt"){
                            const playSavedPromptUsedIn = ObjectsUsedIn.getObjectUsedIn(object.id, commonModule);

                            playSavedPromptUsedIn[1].forEach(objId => {
                                const savePrompt = commonModule.components.objects.find(obj => obj.id === objId);
                                
                                savePrompt.props.config.playSavedPromptId = newObjId;

                                // replace in flows
                                const savedPromptUsedIn = ObjectsUsedIn.getObjectUsedIn(savePrompt.id, commonModule);
                                
                                savedPromptUsedIn[0].forEach(modId => {
                                    const mod = commonModule.components.modules.find(mod => mod.id === modId);
                                    const savePromptNodes = mod.versions[1].flow.nodes.filter(node => node.props.nodeTypeName === 'Save Prompt');

                                    savePromptNodes.forEach(spn => {
                                        if(spn.props.config.playSavedPromptId === object.id){
                                            spn.props.config.playSavedPromptId = newObjId;
                                        }
                                    })
                                })

                            })

                        }
                    });
                })
            };

            // usedInObjects
            if(usedIn[1].length > 0) {}

            // CM do not use these usedInGlobalIntents
            if(usedIn[2].length > 0) {}
            
            // usedInLocalVariables
            if(usedIn[3].length > 0) {}

            // usedInModuleInputs
            if(usedIn[4].length > 0) {}

            // CM do not use these usedInSampleDialogs
            if(usedIn[5].length > 0) {}

            // reset component object Id
            object.id = newObjId;
            // CM TODO: do we show Prompts or other objects in host application panel?
            object.isHidden = true;
            object.props.moduleId = newRootModuleId;
            object.props.moduleApp = this.moduleAppName;

        });

    }

    resetComponentIntents(commonModule){
        const newRootModuleId = this.newRootModuleId;
        
        commonModule.components.intents.forEach(int => {
            const newId = Utility.uuid4();
            const intId = int.id;
            const intentUsedIn = IntentUsedIn.getIntentUsedIn(int, commonModule);
            
            if(intentUsedIn['intentsUsedInInputNodes'].length > 0){
                intentUsedIn['intentsUsedInInputNodes'].forEach(obj => {
                    // update the Input object's reference to intent id
                    const newLocalIntents = obj.node.props.config.localIntents.filter(item => item !== intId);
                    
                    newLocalIntents.push(newId);
                    obj.node.props.config.localIntents = newLocalIntents;
                    
                    // update all input objs canvas instances
                    const inputNodeUsedIn = ObjectsUsedIn.getObjectUsedIn(obj.node.id, commonModule);
                    const inputNodeUsedInFlow = inputNodeUsedIn[0] || [];

                    inputNodeUsedInFlow.forEach(modId => {
                        const mod = commonModule.components.modules.find(mod => mod.id === modId);
                        const inputNodes = mod.versions[1].flow.nodes.filter(node => node.props.nodeTypeName === 'Input');

                        inputNodes.forEach(node => {
                            if (node.props.config.localIntents && node.props.config.localIntents.indexOf(intId) !== -1){
                                const idx = node.props.config.localIntents.indexOf(intId);
                                node.props.config.localIntents.splice(idx, 1);
                                node.props.config.localIntents.push(newId);
                            }

                            if(node.props.config.expectedIntent && node.props.config.expectedIntent === intId){
                                node.props.config.expectedIntent = newId;
                            }

                            if(node.props.config.dtmfIntent && node.props.config.dtmfIntent === intId){
                                node.props.config.dtmfIntent = newId;
                            }

                            if(node.props.config.impliedIntent && node.props.config.impliedIntent === intId){
                                node.props.config.impliedIntent = newId;
                            }
                        })
                    });
                });
            }

            if(intentUsedIn['intentsUsedInDtmfNodes'].length > 0){
                // TODO
                
                intentUsedIn['intentsUsedInDtmfNodes'].forEach(dtmf => {
                    const newSelections = dtmf.node.props.config.selections.map(selection => {
                        if(selection.value === intId){
                            return {
                                key: selection.key,
                                value: newId
                            }
                        } else {
                            return selection;
                        }
                    });

                    dtmf.node.props.config.selections = newSelections;

                })
            }


            int.isHidden = true;
            int.id = newId;
            int.props.moduleId = newRootModuleId;
            int.props.moduleApp = this.moduleAppName;
            int.props.parentComponentID = newId;

        });
    }
    mergeIntentsCacheConfig(commonModule){
        commonModule.components.intents.forEach(int => {
            const cacheIntent = this.cacheIntents.find(ci => ci.name === int.name);
            if(cacheIntent) {
                int.props.config = {...cacheIntent?.props?.config, ...int.props.config};
            }
        });
    }
    //check if variables need scope added
    upgradeVariableCheck(commonModule){
        const { isOmniChannel } = window.apm.serialized.omniChannel;
        
        if(isOmniChannel){
            window.apm.knowledgeBase.addScopeToAppVariables(commonModule.components.variables);
        }
    }

    resetGraphqlSchemasIds(commonModule){
        if(commonModule.graphQLSchemas){
            commonModule.graphQLSchemas.forEach(graphQlSchema=>{    
                const newSchemaId = Utility.uuid4();
                const dataAccessObjects = commonModule.components.objects.filter(obj=>obj._class === "DataNodeModel");
                dataAccessObjects.forEach(obj=>{
                    if(obj._class === "DataNodeModel" && obj.props.config.schema === graphQlSchema.id){
                        const objUsedIn = ObjectsUsedIn.getObjectUsedIn(obj.id, commonModule);
                        // usedInFlows
                        objUsedIn[0].forEach(modId=>{
                            const module = commonModule.components.modules.find(m=>m.id===modId);
                            module.versions[1].flow.nodes.forEach(node=>{
                                if(node.props.parentComponentID === obj.id){
                                    node.props.config.schema = newSchemaId;
                                }
                            })
                        })
                        obj.props.config.schema = newSchemaId;
                    }
                })
                graphQlSchema.id = newSchemaId;
            });
        }
        
    }
    resetCsvDataIds(commonModule){
        if(commonModule.csvData){
            commonModule.csvData.forEach(csvData=>{    
                const newCsvDataId = Utility.uuid4();
                const tabNodeObjects = commonModule.components.objects.filter(obj=>obj._class === "TabNodeModel");
                tabNodeObjects.forEach(obj=>{
                    if(obj._class === "TabNodeModel" && obj.props.config.csvData === csvData.id){
                        const objUsedIn = ObjectsUsedIn.getObjectUsedIn(obj.id, commonModule);
                        // usedInFlows
                        objUsedIn[0].forEach(modId=>{
                            const module = commonModule.components.modules.find(m=>m.id===modId);
                            module.versions[1].flow.nodes.forEach(node=>{
                                if(node.props.parentComponentID === obj.id){
                                    node.props.config.csvData = newCsvDataId;
                                }
                            })
                        })
                        obj.props.config.csvData = newCsvDataId;
                    }
                })
                csvData.id = newCsvDataId;
            });
        }
        
    }
    maintainModuleFoldersAndDescription(previousCommonModule, newModuleId) {
        const newModuleIndex = window.apm.serialized.components.modules.findIndex(mod => mod.id === newModuleId);
        
        window.apm.serialized.components.modules[newModuleIndex].props.config.description = previousCommonModule.props.config.description;
        
        window.apm.serialized.folders.modules.forEach(folder => {
            const moduleIndex = folder.children.findIndex(module => module === previousCommonModule.id);
            if (moduleIndex !== -1) {
                folder.children.splice(moduleIndex, 1);
                folder.children.push(newModuleId);

                window.apm.refreshApplicationTree()
            }
        });
    }
    
}